import { FrameEmptyState } from "@/components/shapes/shared/frame-empty-state";
import type { ReactNode } from "react";
import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { SkeletonReveal } from "@/components/ui";
import { useFrameSurface } from "../shared/use-frame-surface";
import { useT } from "@/i18n";
import { slackFrameMessages } from "@/i18n/messages/frames-slack";

interface SlackFrameMessage {
  id: string;
  user: string;
  initials: string;
  color: string;
  time: string;
  text: string;
  threadReplies?: number;
}

function toSlackMessage(raw: unknown, index: number): SlackFrameMessage | null {
  if (typeof raw !== "object" || raw === null) return null;
  const m = raw as Record<string, unknown>;
  if (typeof m.text !== "string" || typeof m.user !== "string") return null;
  return {
    id: typeof m.id === "string" ? m.id : `msg-${index}`,
    user: m.user,
    initials: typeof m.initials === "string" ? m.initials : "?",
    color: typeof m.color === "string" ? m.color : "var(--color-muted)",
    time: typeof m.time === "string" ? m.time : "",
    text: m.text,
    threadReplies:
      typeof m.threadReplies === "number" ? m.threadReplies : undefined,
  };
}

/** Keep per-user avatar colors safe for inline use; else neutral gray. */
function safeAvatarColor(color: string): string {
  return /^#[0-9a-fA-F]{3,8}$/.test(color) ? color : "var(--color-muted)";
}

function safeInitials(initials: string): string {
  return initials.replace(/[^a-zA-Z0-9]/g, "").slice(0, 3) || "?";
}

/**
 * Minimal mrkdwn renderer (*bold*, `code`, <url|label>) as React nodes —
 * no HTML parsing, so message text can never inject markup.
 */
function renderMrkdwn(text: string): ReactNode[] {
  const parts = text.split(
    /(\*[^*\n]+\*|<https?:\/\/[^<>\s|]+(?:\|[^<>]+)?>|`[^`\n]+`)/g,
  );
  return parts.map((part, index) => {
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
      return (
        <strong key={index} className="font-semibold text-primary">
          {part.slice(1, -1)}
        </strong>
      );
    }
    if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
      return (
        <code
          key={index}
          className="rounded bg-glass-fill-dense px-1 font-mono text-appkit-small text-primary"
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    const link = part.match(/^<(https?:\/\/[^<>\s|]+)(?:\|([^<>]+))?>$/);
    if (link) {
      const href = link[1] ?? part;
      const label = link[2] ?? href;
      return (
        <a
          key={index}
          href={href}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="nodrag text-secondary hover:underline"
          data-testid={`slack-frame-link-${index}`}
        >
          {label}
        </a>
      );
    }
    return <span key={index}>{part}</span>;
  });
}

/**
 * Production SlackFrame — read-only Slack channel view.
 *
 * Same widget recipe as the chat frame (glass surface, text-canvas
 * scale, bg-elevated rows): avatar + name/time header, mrkdwn body,
 * thread-reply row. Rows scroll inside the fixed 400x300 card. Links
 * open directly (target=_blank, no preventDefault hack). No
 * FrameHeader/FrameFooter: canvas drag uses the hover chrome drag zone,
 * and follow-ups/comments/resize/delete come free via the wrap() HOC
 * chain + hover chrome. Content renders from data.content
 * ({channel, messages}); agent frame.update broadcasts re-render
 * automatically.
 */
export function SlackFrame({ id, data, selected }: FrameProps) {
  const t = useT(slackFrameMessages);
  const frameSurface = useFrameSurface(id);
  const content = data.content ?? {};
  const channel = (content.channel as string | undefined) ?? "";
  const rawMessages = Array.isArray(content.messages)
    ? content.messages
    : undefined;
  const messages =
    rawMessages !== undefined
      ? rawMessages
          .map((raw, index) => toSlackMessage(raw, index))
          .filter((m): m is SlackFrameMessage => m !== null)
      : [];
  const status = data.status;
  const isEmpty = messages.length === 0;

  return (
    <div
      className={cn(
        frameSurface.className,
        "group relative flex h-full flex-col overflow-hidden",
        selected
          ? "border-[var(--frame-selected-stroke)]"
          : "border-glass-stroke",
      )}
      data-testid="slack-frame"
      {...frameSurface.attributes}
    >
      <div
        className="nodrag flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto p-4 [--denk-frame-content-padding-block-start:16px] [--denk-frame-content-padding-block-end:16px] [--denk-frame-content-padding-inline:16px]"
        data-frame-content="true"
        onClick={(e) => e.stopPropagation()}
      >
        <SkeletonReveal
          sizeToContent
          loading={isEmpty && status === "streaming"}
          loadingLabel={t("loadingMessages")}
          data-testid="slack-frame-reveal"
          contentClassName="gap-1.5"
          skeleton={
            <div
              className="flex items-start gap-2 py-1"
              data-testid="slack-frame-typing"
              aria-label={t("loadingMessages")}
            >
              <div className="h-7 w-7 shrink-0 rounded-md bg-glass-fill-dense" />
              <div className="flex flex-1 flex-col gap-1.5 pt-0.5">
                <div className="h-2.5 w-24 rounded bg-glass-fill-dense" />
                <div className="h-2.5 w-3/4 rounded bg-glass-fill-dense" />
                <span className="sr-only">{t("loadingMessages")}</span>
              </div>
            </div>
          }
        >
          {!isEmpty && channel && (
            <p
              className="text-appkit-body font-medium text-primary"
              data-testid="slack-frame-channel"
            >
              #{channel.replace(/^#/, "")}
            </p>
          )}
          {isEmpty ? (
            <FrameEmptyState
              frameId={id}
              status={status}
              testId="slack-frame-empty"
            >
              {t("noMessages")}
            </FrameEmptyState>
          ) : (
            messages.map((msg, idx) => (
              <div
                key={msg.id}
                data-testid={`slack-frame-msg-${msg.id}`}
                className="flex min-w-0 shrink-0 gap-2 rounded-preview px-3 py-2"
              >
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-appkit-small font-bold text-white"
                  style={{ backgroundColor: safeAvatarColor(msg.color) }}
                  aria-hidden="true"
                >
                  {safeInitials(msg.initials)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-1.5">
                    <span className="truncate text-appkit-small font-semibold text-primary">
                      {msg.user}
                    </span>
                    <span className="shrink-0 text-appkit-small text-muted">
                      {msg.time}
                    </span>
                  </div>
                  <p className="break-words text-appkit-small leading-relaxed text-secondary">
                    {renderMrkdwn(msg.text)}
                    {idx === messages.length - 1 && status === "streaming" && (
                      <span
                        className="ml-1 inline-block h-2.5 w-0.5 animate-pulse rounded-sm bg-accent align-middle"
                        aria-label={t("writing")}
                      />
                    )}
                  </p>
                  {typeof msg.threadReplies === "number" &&
                    msg.threadReplies >= 0 && (
                      <div
                        className="mt-1 flex items-center gap-1 text-appkit-small text-muted"
                        data-testid={`slack-frame-thread-${msg.id}`}
                      >
                        <Icons.MessageCircle
                          className="h-3 w-3"
                          aria-hidden="true"
                        />
                        {t("replies", { count: msg.threadReplies })}
                      </div>
                    )}
                </div>
              </div>
            ))
          )}
        </SkeletonReveal>
      </div>
    </div>
  );
}
